import type { InitPlugin } from "../types";
import { autoGridShape, indexToIjk, mapAxis } from "../topologies/gridIndex";

export const uniformBoxInit: InitPlugin = {
  id: "uniform_box",
  label: "Uniform box",
  tier: "basic",
  description: "Positions ~ U[-1,1]^d",
  init(_params, rng, out) {
    for (let i = 0; i < out.length; i++) out[i] = rng.uniform(-1, 1);
  },
};

export const ringInit: InitPlugin = {
  id: "ring",
  label: "Ring",
  tier: "basic",
  description: "Unit circle in xy (z=0 if 3D)",
  init(params, rng, out) {
    void rng;
    const { n, dim } = params;
    for (let i = 0; i < n; i++) {
      const theta = (2 * Math.PI * i) / n;
      out[i * dim] = Math.sin(theta);
      out[i * dim + 1] = -Math.cos(theta);
      if (dim === 3) out[i * dim + 2] = 0;
    }
  },
};

export const gaussianInit: InitPlugin = {
  id: "gaussian",
  label: "Gaussian",
  tier: "advanced",
  description: "Isotropic normal cluster",
  init(params, rng, out) {
    const { n, dim } = params;
    for (let i = 0; i < n; i++) {
      for (let d = 0; d < dim; d += 2) {
        const u1 = Math.max(rng.next(), 1e-12);
        const u2 = rng.next();
        const r = Math.sqrt(-2 * Math.log(u1)) * 0.5;
        const theta = 2 * Math.PI * u2;
        out[i * dim + d] = r * Math.cos(theta);
        if (d + 1 < dim) out[i * dim + d + 1] = r * Math.sin(theta);
      }
    }
  },
};

/** Uniform on the unit sphere S² (dim=3); falls back to ring for dim=2. */
export const sphereInit: InitPlugin = {
  id: "sphere",
  label: "Sphere (S²)",
  tier: "advanced",
  description: "Uniform on unit sphere (3D); ring in 2D",
  init(params, rng, out) {
    const { n, dim } = params;
    if (dim < 3) {
      ringInit.init(params, rng, out);
      return;
    }
    for (let i = 0; i < n; i++) {
      // Marsaglia: uniform on S²
      let x = 0,
        y = 0,
        s = 2;
      while (s >= 1 || s <= 1e-12) {
        x = rng.uniform(-1, 1);
        y = rng.uniform(-1, 1);
        s = x * x + y * y;
      }
      const z = 1 - 2 * s;
      const scale = 2 * Math.sqrt(1 - s);
      out[i * dim] = x * scale;
      out[i * dim + 1] = y * scale;
      out[i * dim + 2] = z;
    }
  },
};

/** Thin spherical shell radius ~1 with small radial noise. */
export const sphericalShellInit: InitPlugin = {
  id: "spherical_shell",
  label: "Spherical shell",
  tier: "advanced",
  description: "Near-unit shell with tiny radial jitter",
  init(params, rng, out) {
    sphereInit.init(params, rng, out);
    if (params.dim < 3) return;
    const { n, dim } = params;
    for (let i = 0; i < n; i++) {
      const base = i * dim;
      const jitter = 1 + rng.uniform(-0.04, 0.04);
      out[base]! *= jitter;
      out[base + 1]! *= jitter;
      out[base + 2]! *= jitter;
    }
  },
};

/**
 * Spatial camps for tribes demos (Track R UX).
 * Index layout matches `assignTypesBlocks`: type t is a contiguous index block.
 * T=2 → left/right blobs; T>2 → camps on a circle in xy. dim=3 adds real depth.
 */
export const twoCampsInit: InitPlugin = {
  id: "two_camps",
  label: "Two camps",
  tier: "lab",
  description:
    "Family blobs (left/right or ring) — pairs with tribes_* topologies",
  init(params, rng, out) {
    const { n, dim } = params;
    const T = Math.max(1, Math.min(8, params.typeCount | 0 || 2));
    const base = Math.floor(n / T);
    const rem = n % T;
    // Prefix sums: camp starts
    const starts = new Array<number>(T + 1);
    starts[0] = 0;
    for (let t = 0; t < T; t++) {
      starts[t + 1] = starts[t]! + base + (t < rem ? 1 : 0);
    }
    const sep = 0.95;
    const spread = T <= 2 ? 0.42 : Math.max(0.22, 0.55 / Math.sqrt(T));
    const zSpread = dim === 3 ? 0.45 : 0;

    for (let i = 0; i < n; i++) {
      let camp = T - 1;
      for (let t = 0; t < T; t++) {
        if (i < starts[t + 1]!) {
          camp = t;
          break;
        }
      }
      let cx: number;
      let cy: number;
      let cz = 0;
      if (T <= 2) {
        cx = camp === 0 ? -sep : sep;
        cy = 0;
      } else {
        const ang = (2 * Math.PI * camp) / T - Math.PI / 2;
        cx = sep * Math.cos(ang);
        cy = sep * Math.sin(ang);
        if (dim === 3) {
          // Mild vertical stagger so camps don't stack in depth
          cz = 0.35 * Math.sin(ang * 1.5);
        }
      }
      // Isotropic cluster in xy (+ z in 3D)
      const u1 = Math.max(rng.next(), 1e-12);
      const u2 = rng.next();
      const rr = Math.sqrt(-2 * Math.log(u1)) * spread * 0.55;
      const th = 2 * Math.PI * u2;
      const basei = i * dim;
      out[basei] = cx + rr * Math.cos(th);
      out[basei + 1] = cy + rr * Math.sin(th);
      if (dim === 3) {
        const u3 = Math.max(rng.next(), 1e-12);
        const u4 = rng.next();
        const rz = Math.sqrt(-2 * Math.log(u3)) * zSpread * 0.45;
        out[basei + 2] = cz + rz * Math.cos(2 * Math.PI * u4);
      }
    }
  },
};

/** Jittered lattice in [-gridExtent, gridExtent]^d (Track Y4). */
export const gridInit: InitPlugin = {
  id: "grid",
  label: "Grid (lattice)",
  tier: "advanced",
  description: "Jittered lattice sites — pairs with voxel topologies",
  init(params, rng, out) {
    const dim = (params.dim === 3 ? 3 : 2) as 2 | 3;
    const { n } = params;
    const shape = autoGridShape(n, dim);
    const e = params.gridExtent > 0 ? params.gridExtent : 1;
    const j = Math.max(0, params.gridJitter ?? 0.02);
    for (let i = 0; i < n; i++) {
      const [ix, iy, iz] = indexToIjk(i, shape);
      const base = i * dim;
      out[base] = mapAxis(ix, shape.nx, e) + rng.uniform(-j, j);
      out[base + 1] = mapAxis(iy, shape.ny, e) + rng.uniform(-j, j);
      if (dim === 3) {
        out[base + 2] = mapAxis(iz, shape.nz, e) + rng.uniform(-j, j);
      }
    }
  },
};
